import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Save, Lock } from "lucide-react";
import { TopBar, Footer } from "@/components/Brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ARCHETYPE_LABELS = [
  { key: "prover", label: "The Prover", meditation: "You Are Already Enough" },
  { key: "over_responsible", label: "The Over-Responsible One", meditation: "It’s Safe to Release Control" },
  { key: "invisible_achiever", label: "The Invisible Achiever", meditation: "It’s Safe to Be Seen" },
  { key: "self_doubter", label: "The Self-Doubter", meditation: "Trust Yourself Again" },
  { key: "hyper_aware", label: "The Hyper-Aware Achiever", meditation: "It’s Safe to Slow Down" },
];

export default function Admin() {
  const [token, setToken] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [videos, setVideos] = useState({});
  const [leads, setLeads] = useState({ total: 0, with_email: 0, by_archetype: [] });

  const load = async (tok) => {
    setError("");
    try {
      const [v, s] = await Promise.all([
        axios.get(`${API}/admin/videos`, { params: { token: tok } }),
        axios.get(`${API}/admin/stats`, { params: { token: tok } }),
      ]);
      setVideos(v.data || {});
      setLeads(s.data || { total: 0, with_email: 0, by_archetype: [] });
      setLoaded(true);
      if (typeof window !== "undefined") sessionStorage.setItem("clarity_admin_token", tok);
    } catch (e) {
      setError(e?.response?.status === 401 ? "Invalid token." : "Failed to load. Try again.");
      setLoaded(false);
    }
  };

  useEffect(() => {
    const t = typeof window !== "undefined" ? sessionStorage.getItem("clarity_admin_token") : null;
    if (t) {
      setToken(t);
      load(t);
    } else {
      // Try with empty token (when ADMIN_TOKEN env not set, backend allows)
      load("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const r = await axios.post(`${API}/admin/videos`, videos, { params: { token } });
      setVideos(r.data || {});
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch (e) {
      setError(e?.response?.status === 401 ? "Invalid token." : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const csvUrl = `${API}/admin/leads.csv${token ? `?token=${encodeURIComponent(token)}` : ""}`;

  return (
    <div className="min-h-screen bg-cream">
      <TopBar />
      <main className="px-6 sm:px-10 lg:px-16 pt-6 pb-16">
        <div className="max-w-5xl mx-auto">
          <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-sage-700 mb-3">
            <span className="inline-block h-px w-10 align-middle bg-sage-700/60 mr-3" /> Admin
          </p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-ink mb-3">
            Quiz Control Room
          </h1>
          <p className="font-sans text-base text-ink/70 max-w-2xl mb-10">
            Paste a Loom, YouTube, or Vimeo URL for each archetype. The right video plays automatically based on each visitor’s result. Share URLs are auto-converted to embed format.
          </p>

          {/* Token field if needed */}
          <div className="mb-10 max-w-md">
            <label className="block font-sans text-[10px] tracking-[0.3em] uppercase text-ink/55 mb-2 inline-flex items-center gap-2">
              <Lock className="h-3 w-3" /> Admin token <span className="opacity-50">(leave blank if not set)</span>
            </label>
            <div className="flex gap-2">
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="••••••••"
                className="flex-1 px-4 py-3 bg-transparent border border-cream-300 focus:border-sage-700 outline-none rounded-sm font-sans text-ink"
                data-testid="admin-token-input"
              />
              <button
                type="button"
                onClick={() => load(token)}
                className="bg-ink text-cream-100 px-5 py-3 rounded-sm font-sans text-xs tracking-[0.18em] uppercase hover:bg-sage-700"
                data-testid="admin-unlock-button"
              >
                Unlock
              </button>
            </div>
            {error && <p className="font-sans text-sm text-red-700 mt-2" data-testid="admin-error">{error}</p>}
          </div>

          {/* Lead stats */}
          {loaded && (
            <div className="grid sm:grid-cols-3 gap-4 mb-12">
              <div className="p-5 bg-cream-100 border border-cream-300 rounded-sm" data-testid="admin-stat-total">
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-ink/55 mb-2">Total quizzes</p>
                <p className="font-display text-4xl text-ink">{leads.total}</p>
              </div>
              <div className="p-5 bg-cream-100 border border-cream-300 rounded-sm" data-testid="admin-stat-emails">
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-ink/55 mb-2">With email</p>
                <p className="font-display text-4xl text-ink">{leads.with_email}</p>
              </div>
              <div className="p-5 bg-cream-100 border border-cream-300 rounded-sm">
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-ink/55 mb-2">Export</p>
                <a href={csvUrl} className="font-display text-xl text-sage-700 link-reveal" data-testid="admin-csv-link">Download CSV →</a>
              </div>
            </div>
          )}

          {/* Video URL form */}
          {loaded && (
            <form onSubmit={onSave} className="space-y-5" data-testid="admin-video-form">
              {ARCHETYPE_LABELS.map((a) => (
                <div key={a.key} className="grid sm:grid-cols-12 gap-4 items-center p-5 bg-cream-100 border border-cream-300 rounded-sm">
                  <div className="sm:col-span-4">
                    <p className="font-display text-xl text-ink">{a.label}</p>
                    <p className="font-sans text-xs text-ink/55 mt-0.5 italic-display">{a.meditation}</p>
                  </div>
                  <div className="sm:col-span-8">
                    <input
                      type="url"
                      value={videos[a.key] || ""}
                      onChange={(e) => setVideos({ ...videos, [a.key]: e.target.value })}
                      placeholder="https://www.loom.com/share/… or YouTube / Vimeo URL"
                      className="w-full px-4 py-3 bg-transparent border border-cream-300 focus:border-sage-700 outline-none rounded-sm font-sans text-sm text-ink"
                      data-testid={`admin-video-input-${a.key}`}
                    />
                  </div>
                </div>
              ))}

              <div className="pt-3 flex items-center gap-5">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-3 bg-ink text-cream-100 px-7 py-4 rounded-sm font-sans text-sm tracking-[0.18em] uppercase hover:bg-sage-700 transition-colors disabled:opacity-60"
                  data-testid="admin-save-button"
                >
                  {saving ? "Saving…" : "Save Video URLs"}
                  <Save className="h-4 w-4" />
                </button>
                {saved && (
                  <span className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-sage-700" data-testid="admin-saved-toast">
                    <CheckCircle2 className="h-4 w-4" /> Saved
                  </span>
                )}
              </div>
            </form>
          )}

          <div className="mt-16 p-6 bg-cream-100 border border-cream-300 rounded-sm">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-sage-700 mb-2">Pro tip</p>
            <p className="font-sans text-sm text-ink/75 max-w-2xl">
              Record one Loom per archetype. Open Beat 1 with their archetype name (e.g., “Hey — if you got The Prover…”). Same Beats 2–5 for all five. Five videos, ~10 minutes of filming, measurable conversion lift.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
